import { distinctUntilChanged, map, filter } from 'rxjs/operators';
import { Observable, BehaviorSubject, switchMap, of } from "rxjs";
import { UserRoleType } from "./../../models/userRoleTypes";
import { PermissionModule } from './../../models/auth';
import { AuthService } from './../http/auth.service';
import { UserScope } from './../../models/auth';
import { JwtHelperService } from '@auth0/angular-jwt';
import { computed, Injectable } from "@angular/core";
import { EntityType } from 'src/app/models/entityTypes';
import { EntityRelationListingItem } from "../../models/userRelationships";

@Injectable({
  providedIn: 'root'
})
export class TokenService {

  private accessibleEntityTypes: EntityType[] = [EntityType.Investor, EntityType.UnitisedFund];
  isAdministratorSubject: BehaviorSubject<boolean> = new BehaviorSubject(null);

  jwtHelper: JwtHelperService;
  claims: any;
  public claims$: Observable<any> = this.authService.authToken$.pipe(
    distinctUntilChanged(),
    map((token: string) => {
      return (token) ? this.jwtHelper.decodeToken(token) : null;
    }));

  public claimsNotNull$: Observable<any> = this.claims$.pipe(
    filter((claims: any) => {
      return claims !== null;
    })
  );

  constructor(private authService: AuthService) {
    this.jwtHelper = new JwtHelperService();

    this.claims$.pipe(
      filter((claims: any) => {
        return !!claims;
      })).subscribe((claims: any) => {
        this.claims = claims;
      });
  }

  getManagerId(): number {
    return (this.claims && this.claims['primarygroupsid']) ? this.claims['primarygroupsid'] : null;
  }

  getLoggedInUserId(): number {
    return (this.claims && this.claims['primarysid']) ? this.claims['primarysid'] : null;
  }

  getRole(): number[] {
    if (!this.claims || !this.claims['role']) {
      return null;
    }
    if (Array.isArray(this.claims['role'])) {
      return this.claims['role'][0].split(',').map(x => Number(x));
    }
    return this.claims['role'].split(',').map(x => Number(x));
  }

  isAdministrator(): boolean {
    const validTypes: UserScope[] = ['Portfolio Administration', 'Investment Management Viewer'];
    let fRet = false;
    if (this.claims && this.claims['user-scope']) {
      let userScope: UserScope | UserScope[];
      userScope = this.claims['user-scope'];
      fRet = Array.isArray(userScope) ? userScope.some(scope => validTypes.includes(scope)) : validTypes.includes(userScope);
    }
    return fRet;
  }
  

  checkPermission(bitwiseFlag: PermissionModule): boolean {
    if (this.claims) {
      const bitwiseMask = (this.claims['module-scope']) ? this.claims['module-scope'] : PermissionModule.None;
      return (bitwiseMask & bitwiseFlag) !== 0;
    }
    /**
    this is done by using bitwise & (AND) operator, MASK & ANY_FLAG. If result is anything other than Falsy zero, the flag is in the mask.
    For more info about bitwise flags and bitmasks : https://blog.rinatussenov.com/juggling-bits-in-javascript-bitmasks-128ad5f31bed
    */
  }

  isUserInRelationToMultiplePortfolios() {
    return this.isAdministrator() || this.authService.doesUserHaveRole(UserRoleType.Advisor) || this.authService.getUserRelationships()?.length > 0;
  }

  isUserInRelationToSingleNonInvestorEntity(): boolean {
    let userRelationships = this.authService.getUserRelationships();
    
    if (userRelationships == null || userRelationships.length > 0) {
      return false;
    }
    
    if(this.authService.userRelationships()?.entityRelationships && this.authService.userRelationships()?.entityRelationships.length == 1){
      const firstEntityRelationship = this.authService.getFirstEntityRelationship();
      const baseEntityType = firstEntityRelationship.baseEntityType;

      return baseEntityType !== EntityType.Investor && this.accessibleEntityTypes.includes(baseEntityType);  
    }
    return false;
  }

  public getFirstEntityRelationship(): EntityRelationListingItem {
    return this.authService.getFirstEntityRelationship();
  }

  hasAssumedUser(): boolean {
    return this.claims && this.claims['primarysid'];
  }
}
